/**
 * Arcade physics step for the player car.
 *
 * Source of truth: `docs/gdd/10-driving-model-and-physics.md`. Numeric
 * defaults derive from `docs/gdd/11-cars-and-stats.md` and
 * `docs/gdd/23-balancing-tables.md` (per-car `baseStats`).
 *
 * The step is a pure function:
 *
 *   step(state, input, context, dt) -> state
 *
 * No globals, no time source, no RNG. Same arguments produce identical
 * outputs across runs, which is what the §21 replay/ghost system requires
 * (AGENTS.md RULE 8 "Determinism is mandatory").
 *
 * Coordinate conventions:
 * - `z` is forward distance along the track centerline, in meters. Always
 *   non-decreasing while the car is moving forward.
 * - `x` is lateral offset from the road centerline, in meters. Negative is
 *   left, positive is right. The drivable surface spans
 *   `[-ROAD_WIDTH, +ROAD_WIDTH]`; outside that band is off-road.
 * - `speed` is forward speed in m/s. Always non-negative for the MVP.
 *   Reverse is not modelled in this slice (see Phase 2 followup).
 *
 * MVP scope: this slice covers acceleration, top-speed clamp, brake,
 * lane-relative steering, and off-road slowdown. Collisions, traction
 * loss, drifting, jumps, nitro, weather, and damage are all tracked as
 * later slices per `docs/IMPLEMENTATION_PLAN.md`. The state shape is
 * intentionally minimal so those slices can extend it additively
 * without a breaking rewrite.
 *
 * Drafting bonus: the optional `draftBonus` field on `StepOptions` is a
 * multiplicative scalar applied to the throttle-driven acceleration only.
 * Computed by the pure helpers in `./drafting.ts`. Defaults to 1.0
 * (no bonus) so existing callers keep their behaviour.
 */

import type { CarBaseStats } from "@/data/schemas";
import { ROAD_WIDTH } from "@/road/constants";
import type { DamageScalars } from "./damageBands";
import { PRISTINE_SCALARS } from "./damageBands";
import type { AssistScalars } from "./difficultyPresets";
import type { Input } from "./input";

/**
 * Identity (Balanced) §28 scalars used as the default when no preset has
 * been resolved. Mirrors `getPreset("normal")` from `./difficultyPresets`,
 * inlined here so the physics module does not pull a runtime dependency on
 * the preset table just to express the no-op identity. Kept frozen so a
 * caller cannot accidentally mutate the shared default.
 *
 * The only field that diverges from a literal `1.0` row is
 * `steeringAssistScale`, which the §28 Balanced row pins at `0.10`. The
 * physics step ignores `steeringAssistScale` entirely when no
 * `assistScalars` are passed (`undefined` short-circuits the assist
 * branch), so this constant is only consumed when an explicit
 * `assistScalars` reference has been forwarded.
 */
const IDENTITY_ASSIST_SCALARS: Readonly<AssistScalars> = Object.freeze({
  steeringAssistScale: 0,
  nitroStabilityPenalty: 1,
  damageSeverity: 1,
  offRoadDragScale: 1,
});

/**
 * Logical version of the physics math. Bumped any time the integration
 * order, the constants, or the equation shape changes. Separate from the
 * build-version slice (which stamps the git SHA at build time): this one
 * is owned by code, lives in source, and is what `src/game/ghost.ts` (the
 * ghost replay slice) writes into recorded replays so a replay produced
 * under v1 physics is rejected when the math has moved on.
 *
 * Bump rules:
 *
 *   - Adding a new field to `CarState` that physics writes to: bump.
 *   - Changing any constant in this file or in `damageBands.ts` that
 *     `step()` consumes: bump.
 *   - Re-ordering the integration sub-steps (accel before brake vs the
 *     other way around): bump.
 *   - Adding a new optional `StepOptions` field with a default that
 *     preserves prior outputs (drafting bonus default 1.0 was such a
 *     change): no bump needed.
 *
 * The cheap discipline is "if in doubt, bump". A spurious bump only
 * costs the player their old ghost; a missing bump corrupts replays
 * silently.
 *
 * Version history:
 *   - 1: pre-§28 wiring. `step()` ignored difficulty preset scalars; off-
 *     road drag and steering authority were fixed regardless of player
 *     setting.
 *   - 2: §28 preset scalars (`offRoadDragScale`, `steeringAssistScale`)
 *     consumed when `StepOptions.assistScalars` is forwarded. A ghost
 *     recorded under v1 cannot be replayed verbatim because a v2 race
 *     session resolves and forwards the player's preset scalars; the
 *     bump rejects v1 ghosts so the §20 PB compare layer never compares
 *     across pre-binding and post-binding math.
 *   - 3: Off-road drag now applies to incoming speed before throttle.
 *     This preserves grass slowdown while allowing a stopped off-road
 *     car to launch under throttle instead of being clamped back to
 *     zero by same-frame drag.
 *   - 4: Lateral integration now multiplies `lateralVelocity` by `dt`
 *     so the term is m/s integrated over the tick rather than m/tick.
 *     The pre-fix code crossed the half-road in roughly 60 ms at top
 *     speed full steer (60x too fast at 60 Hz). v3 ghost replays are
 *     invalidated because the lateral path differs.
 */
export const PHYSICS_VERSION = 4;

/**
 * Off-road handling tunables from §10 "Suggested tunable constants".
 *
 * These are the starter-tier values from the table. Mid and late tier
 * variants exist; we plumb a single set here and let the per-car stats
 * layer decide whether to override later. Off-road behaviour is mostly
 * grip-class agnostic in §10, so a shared constant is faithful to the
 * design until a balancing slice proves otherwise.
 */
export const OFF_ROAD_CAP_M_PER_S = 24;
export const OFF_ROAD_DRAG_M_PER_S2 = 18;

/**
 * Multiplier applied to `roadHalfWidth` to define the outer edge of the
 * rumble band. Matches the strip drawer's `near.screenW * 1.15` rumble
 * trapezoid in `pseudoRoadCanvas.drawStrips`. The drivable surface is
 * `[-roadHalfWidth, +roadHalfWidth]`; the rumble band sits in
 * `(roadHalfWidth, roadHalfWidth * RUMBLE_HALF_WIDTH_SCALE]` on each side;
 * grass is anything beyond.
 *
 * Why expose the constant rather than re-deriving from the drawer? The
 * physics layer must agree with the renderer on what counts as rumble vs
 * grass so dust particles only emit when the player is visually on grass.
 * Pinning the scalar here keeps the two layers in lockstep without
 * importing the renderer (which would invert the dependency direction).
 */
export const RUMBLE_HALF_WIDTH_SCALE = 1.15;

/**
 * Surface the car is on this tick. Derived from `|car.x|` against
 * `roadHalfWidth`. `road` covers the drivable surface, `rumble` the band
 * just outside, `grass` everything beyond.
 *
 * The renderer's dust pool consumes this flag to decide whether to emit
 * particles; a future surface-audio slice can dispatch tyre-rumble SFX
 * off the same flag without re-deriving the geometry.
 */
export type Surface = "road" | "rumble" | "grass";

/**
 * Coast (no throttle, no brake) drag in m/s^2. §10 lists 4.5 / 4.0 / 3.5
 * across starter/mid/late tiers; we use the starter value for the MVP.
 */
export const COASTING_DRAG_M_PER_S2 = 4.5;

/**
 * Steering rate band, in radians per second. §10 lists three tiers; the
 * starter values are used for the MVP. The "rate" here is the angular
 * authority of the front wheels, not the heading; we translate it into a
 * lateral velocity contribution in `step()` so the car drifts toward the
 * side the driver is steering.
 *
 * Why an angular rate? §10 specifies `yawDelta = steerInput * steerRate
 * * dt * tractionScalar`. The MVP renderer does not show yaw, so we
 * project the yaw delta onto a lateral velocity by multiplying by the
 * forward speed. That keeps the §10 equation intact while producing the
 * lane-relative behaviour the dot calls for.
 */
export const STEER_RATE_LOW_RAD_PER_S = 2.3;
export const STEER_RATE_HIGH_RAD_PER_S = 1.25;

/**
 * Speed at which steering response transitions from "low" (tight) to
 * "high" (subtle). The §10 lerp uses `speedNorm` against the car's top
 * speed; we keep that ratio here.
 */
function steerRateForSpeed(speed: number, topSpeed: number): number {
  if (topSpeed <= 0) return STEER_RATE_LOW_RAD_PER_S;
  const speedNorm = clamp(speed / topSpeed, 0, 1);
  return lerp(STEER_RATE_LOW_RAD_PER_S, STEER_RATE_HIGH_RAD_PER_S, speedNorm);
}

/**
 * Pure car kinematic state. Extended additively by later slices
 * (heading, traction, nitro charges, damage). Nothing in this slice
 * stores time references; the loop owns the clock.
 *
 * `surface` is the surface classification the car is on as of the END of
 * the most recent step. `INITIAL_CAR_STATE` pins it to `"road"` so a
 * fresh state at the centerline reads consistently before any tick has
 * run. Pure consumers (dust, future surface SFX) should treat this as
 * the canonical signal rather than re-deriving from `x`.
 */
export interface CarState {
  /** Forward distance along track centerline in meters. */
  z: number;
  /** Lateral offset from centerline in meters. Negative = left. */
  x: number;
  /** Forward speed in m/s. Always >= 0 in the MVP. */
  speed: number;
  /** Surface classification at the end of the most recent step. */
  surface: Surface;
}

/** Initial state convenience: stationary at the centerline at z=0. */
export const INITIAL_CAR_STATE: Readonly<CarState> = Object.freeze({
  z: 0,
  x: 0,
  speed: 0,
  surface: "road",
});

/**
 * Track context the physics layer needs from the renderer. Kept narrow on
 * purpose: passing the full `Track` would couple physics to data-schemas
 * and force tests to construct full tracks for trivial cases.
 *
 * `roadHalfWidth` is the lateral half-width of the drivable surface. The
 * default is `ROAD_WIDTH` from the renderer. Tracks may override it once
 * the data schema gains a per-track override field.
 */
export interface TrackContext {
  roadHalfWidth: number;
}

/** Default context using the renderer's `ROAD_WIDTH` constant. */
export const DEFAULT_TRACK_CONTEXT: Readonly<TrackContext> = Object.freeze({
  roadHalfWidth: ROAD_WIDTH,
});

/**
 * Optional per-tick modifiers to the physics step. Kept separate from
 * `TrackContext` because these vary per-tick (drafting), per-driver
 * (damage band), or per-input frame, while `TrackContext` is fixed for
 * the duration of a race.
 *
 * `draftBonus` is a multiplicative scalar applied to the throttle-driven
 * acceleration. The pure helpers in `./drafting.ts` compute the value;
 * passing `1` (or omitting the field entirely) means "no bonus". The
 * field is clamped to a sane band (`[1, 1.5]`) inside `step()` so a buggy
 * caller cannot turn a draft bonus into a top-speed override.
 *
 * `damageScalars` are the per-band performance multipliers from
 * `./damageBands.ts`. The race session resolves the band from the
 * driver's current `DamageState.total` and passes the scalars in. When
 * omitted, `step()` uses `PRISTINE_SCALARS` so existing callers keep
 * their behaviour. Only `topSpeedScalar` and `gripScalar` are read
 * inside `step()` today: the §10 "stability", "nitroEfficiency", and
 * "spinRiskMultiplier" knobs are owned by their respective slices
 * (steering smoothing, nitro system, traction loss) and read the
 * scalars off the same field without a second resolve.
 *
 * `accelMultiplier` is the gear-curve scalar from
 * `./transmission.ts:gearAccelMultiplier`. Multiplied against the
 * throttle-driven acceleration each tick to model the §10 torque curve
 * (lugging at low RPM, peak in the middle of the band, redline penalty
 * past the soft limit). Defaults to `1.0` so existing callers keep
 * their behaviour. Clamped to a sane band (`[0, 2]`) so a buggy
 * upstream reducer cannot turn the field into a top-speed override.
 */
export interface StepOptions {
  draftBonus?: number;
  damageScalars?: Readonly<DamageScalars>;
  accelMultiplier?: number;
  /**
   * §28 difficulty preset scalars. When present, the step scales
   * `OFF_ROAD_DRAG_M_PER_S2` by `offRoadDragScale` (so an Easy preset
   * with a `1.20` scalar bites harder off-road, while Hard / Master
   * relax the punishment) and applies `steeringAssistScale` as a pull
   * toward the centerline on the lateral velocity contribution (so an
   * Easy preset with `0.25` scrubs 25 percent of oversteer toward
   * neutral each tick). Omitting the field preserves the unscaled
   * pre-§28 behaviour exactly; any caller that has not yet wired the
   * preset can keep its existing `step()` invocation. The race session
   * resolves the active preset once at session creation and threads
   * the same frozen reference through every per-tick call so there is
   * no per-tick allocation cost.
   */
  assistScalars?: Readonly<AssistScalars>;
  /**
   * Per-tick weather grip scalar, composed by the race session from
   * `weatherGripScalar` in `weather.ts`. Defaults to `1` so callers
   * that have not wired weather preserve their old dry-grip output.
   */
  weatherGripScalar?: number;
}

/** Conservative upper bound on the draft bonus inside the step. */
export const DRAFT_BONUS_MAX = 1.5;

/**
 * Conservative upper bound on the accel multiplier inside the step. The
 * §10 torque curve peaks at `MANUAL_PEAK_TORQUE_MULTIPLIER = 1.04`; this
 * defends against a buggy reducer that returns a wildly out-of-band
 * value without throwing.
 */
export const ACCEL_MULTIPLIER_MAX = 2;

/**
 * Advance the car state by `dt` seconds. Pure: no mutation of the input
 * `state`; a fresh object is returned even when nothing changes. Callers
 * should treat the result as the canonical next state.
 *
 * Edge cases handled here (per the dot's "Edge Cases" section):
 * - `dt <= 0`: state unchanged. The loop never feeds us negative dt, but
 *   defending against zero keeps the function safe for unit tests that
 *   want to evaluate "did anything happen this frame".
 * - `speed` clamped at `stats.topSpeed`. Acceleration cannot push past it.
 * - Brake while at zero speed does not invert the velocity. We treat
 *   "brake" as deceleration toward zero, never below.
 * - Steering at zero speed produces no lateral movement: the §10 yaw
 *   formula is multiplied by `speed` so the car cannot crab sideways.
 * - Off-road for one frame: reduces traction (via `gripDry` halving) and
 *   applies extra drag, but no damage. Damage is a later slice.
 */
export function step(
  state: Readonly<CarState>,
  input: Readonly<Input>,
  stats: Readonly<CarBaseStats>,
  context: Readonly<TrackContext>,
  dt: number,
  options: Readonly<StepOptions> = {},
): CarState {
  if (!Number.isFinite(dt) || dt <= 0) {
    return {
      z: state.z,
      x: state.x,
      speed: state.speed,
      surface: state.surface,
    };
  }

  const offRoad = isOffRoad(state.x, context.roadHalfWidth);

  // Longitudinal: integrate throttle, brake, drag, then clamp.
  let nextSpeed = state.speed;
  const throttle = clamp(input.throttle, 0, 1);
  const brake = clamp(input.brake, 0, 1);
  const draftBonus = clamp(options.draftBonus ?? 1, 1, DRAFT_BONUS_MAX);
  // Gear torque curve: clamp into a sane band defensively. Default is
  // `1` so existing callers (and any code path that does not run the
  // transmission reducer) keep the pre-transmission behaviour.
  const accelMultiplier = clamp(options.accelMultiplier ?? 1, 0, ACCEL_MULTIPLIER_MAX);
  // Damage scalars: clamp each consumed field defensively. A buggy
  // upstream caller cannot turn a damage scalar into a speed boost; the
  // upper bound for `topSpeedScalar` and `gripScalar` is 1.0.
  const damageScalars = options.damageScalars ?? PRISTINE_SCALARS;
  const topSpeedScalar = clamp(damageScalars.topSpeedScalar, 0, 1);
  const damagedTopSpeed = stats.topSpeed * topSpeedScalar;

  // §28 difficulty preset scalars: identity when no preset is forwarded
  // so existing call sites keep their pre-binding behaviour. Each scalar
  // is clamped defensively against the §28 documented bands so a buggy
  // upstream config cannot push the off-road drag into a divide-by-zero
  // band or steer the assist past the §10 stability ceiling.
  const presetScalars = options.assistScalars ?? IDENTITY_ASSIST_SCALARS;
  const offRoadDragScale = clamp(presetScalars.offRoadDragScale, 0, 4);
  const steeringAssistScale = clamp(presetScalars.steeringAssistScale, 0, 1);

  if (offRoad) {
    // §10 "Off-road should reduce traction, apply strong drag, cap top speed."
    // §28 `offRoadDragScale` lets the difficulty preset bias how harshly
    // grass / dirt slows the car; Beginner reads `1.20` (sticky off-road
    // keeps the player from short-cutting), Expert reads `0.95` (slight
    // forgiveness so a wide line is not race-ending).
    const dragDelta = OFF_ROAD_DRAG_M_PER_S2 * offRoadDragScale * dt;
    nextSpeed = Math.max(0, nextSpeed - dragDelta);
  }

  if (throttle > 0) {
    nextSpeed += stats.accel * throttle * draftBonus * accelMultiplier * dt;
  }
  if (brake > 0) {
    // Brake decelerates toward zero. Never inverts velocity.
    const delta = stats.brake * brake * dt;
    nextSpeed = Math.max(0, nextSpeed - delta);
  } else if (throttle === 0) {
    // Coasting drag. Decays toward zero only; cannot push us below 0.
    const delta = COASTING_DRAG_M_PER_S2 * dt;
    nextSpeed = Math.max(0, nextSpeed - delta);
  }

  if (offRoad && nextSpeed > OFF_ROAD_CAP_M_PER_S) {
    nextSpeed = OFF_ROAD_CAP_M_PER_S;
  }

  // Top-speed clamp last so accel cannot overshoot via accumulated dt.
  // The damage band shrinks the cap; a heavily damaged car cannot reach
  // its undamaged top speed even with full throttle.
  if (nextSpeed > damagedTopSpeed) {
    nextSpeed = damagedTopSpeed;
  }

  // Lateral: §10 yaw equation, projected onto lateral velocity by speed.
  // Grip on-road uses `gripDry`; off-road halves grip per §10's
  // "reduce traction" requirement. A future weather slice replaces the
  // dry-vs-wet selector; clamp guards against degenerate stat values.
  // The damage band's `gripScalar` derates grip in the moderate band
  // and above per §10 "reduced grip".
  const damageGripScalar = clamp(damageScalars.gripScalar, 0, 1);
  const weatherGripScalar = clamp(options.weatherGripScalar ?? 1, 0, 2);
  const baseGrip =
    clamp(stats.gripDry, 0, 2) * damageGripScalar * weatherGripScalar;
  const tractionScalar = offRoad ? baseGrip * 0.5 : baseGrip;
  const steerInput = clamp(input.steer, -1, 1);
  const steerRate = steerRateForSpeed(nextSpeed, stats.topSpeed);
  const yawDelta = steerInput * steerRate * dt * tractionScalar;
  // Lateral velocity in m/s = yaw rate * forward speed. At zero speed the
  // car cannot move sideways, satisfying the dot's edge case.
  let lateralVelocity = yawDelta * nextSpeed;
  // §28 `steeringAssistScale` clamps the lateral velocity contribution
  // toward neutral. A Beginner preset (`0.25`) scrubs 25 percent of the
  // lateral velocity per tick before integration; Expert / Master read
  // `0.0` (the §10 floor), preserving the unscaled steering authority.
  // The §10 narrative pins this as "the steering-assist authority knob":
  // it does not block intentional lane changes, it dampens the lateral
  // velocity that the steering equation injects each tick. The effect
  // composes with `tractionScalar` so an Easy player on grass still
  // suffers reduced grip; the assist does not promise the car onto the
  // road. Identity (`0.0`) preserves the pre-§28 behaviour bit-for-bit
  // because the multiplier collapses to `1`.
  lateralVelocity = lateralVelocity * (1 - steeringAssistScale);
  // Integrate lateral velocity over the tick. Without the dt multiply the
  // term is m/tick which at 60 Hz is 60x m/s; that bug let the player cross
  // the half-road in roughly 60 ms at top speed full steer. Multiplying by
  // dt closes the unit error so steady-state lateral velocity matches §10
  // at 1.27 m/s post-fix and crosses the 4.5 m half-road in about 3.5 s
  // (further tuned to the Top Gear 2 reference of 1.5 s by the cornering
  // tuning slice).
  const nextX = state.x + lateralVelocity * dt;

  // Forward integration uses the post-update speed. Trapezoidal would be
  // more accurate but the §21 fixed-step loop runs at 60 Hz which keeps
  // per-frame error below visible thresholds.
  const nextZ = state.z + nextSpeed * dt;

  // Classify surface from the post-step lateral position. Done last so the
  // surface field reflects "where the car ended up this tick", which is
  // what the dust pool needs when it samples emissions per tick.
  const nextSurface = surfaceAt(nextX, context.roadHalfWidth);

  return { z: nextZ, x: nextX, speed: nextSpeed, surface: nextSurface };
}

/** True if `x` is outside the drivable surface. */
export function isOffRoad(x: number, roadHalfWidth: number): boolean {
  return Math.abs(x) > roadHalfWidth;
}

/**
 * Pure surface classifier. Bands:
 *   |x| <= roadHalfWidth                            -> "road"
 *   roadHalfWidth < |x| <= roadHalfWidth * 1.15     -> "rumble"
 *   |x| > roadHalfWidth * 1.15                      -> "grass"
 *
 * The rumble upper bound matches the renderer's rumble trapezoid in
 * `pseudoRoadCanvas.drawStrips` (see `RUMBLE_HALF_WIDTH_SCALE`). Edges
 * are inclusive on the road and rumble bands so a car sitting exactly on
 * the half-width line classifies as `road`, mirroring `isOffRoad`'s
 * "strictly greater than" semantics.
 */
export function surfaceAt(x: number, roadHalfWidth: number): Surface {
  const absX = Math.abs(x);
  if (absX <= roadHalfWidth) return "road";
  if (absX <= roadHalfWidth * RUMBLE_HALF_WIDTH_SCALE) return "rumble";
  return "grass";
}

// Numeric helpers ----------------------------------------------------------

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
